/**
 * 1.5.0 shot detection, stream S2: the Log tab's Fill from shots and Remove empty segments (plan §5).
 *
 *  - FillFromShots: per user layer, "Fill from shots" (useFillFromShots; the whole asset, or the player's In–Out when
 *    marked: "38 added, 4 skipped (already logged)") and "Remove empty segments" (asks first; useRemoveEmptySegments),
 *    and the note "Shots changed since this layer was filled" (segments filled from shots whose times no longer match
 *    a shot). Nothing for a person without asset.edit_metadata, or before shots were detected.
 *  - The logging comfort (Enter saves and opens the next empty segment, the player seeks to each, the shot's keyframe
 *    beside the editor) is in LogTab.tsx.
 */
import { useState } from 'react';
import { Eraser, Loader2, ScanLine } from 'lucide-react';
import { SEGMENT_SOURCE_SHOTS, TICKS_PER_SECOND, type AssetDetail, type SegmentDTO, type StratumDTO } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useMe } from '../../api/hooks';
import { shotSegments, useFillFromShots, useRemoveEmptySegments } from '../../api/hooks/shots';
import { toast } from '../../components/Toasts';

/** A segment with nothing logged on it (what Remove empty removes, and where Enter goes next). */
export const isEmptySegment = (s: Pick<SegmentDTO, 'text' | 'values'>) => !s.text.trim() && s.values.length === 0;

/** "38 added, 4 skipped (already logged)". */
export function fillResultText(r: { added: number; skipped: number }): string {
  return `${r.added} added${r.skipped ? `, ${r.skipped} skipped (already logged)` : ''}`;
}

/** Layers with segments filled from shots whose times no longer match a shot: the shots changed since. */
export function layersWithChangedShots(segments: SegmentDTO[], shots: SegmentDTO[]): Set<string> {
  const spans = new Set(shots.map((s) => `${s.startTicks}-${s.endTicks}`));
  const out = new Set<string>();
  if (!shots.length) return out;
  for (const s of segments) {
    if (s.source === SEGMENT_SOURCE_SHOTS && s.stratumId !== shots[0]!.stratumId && !spans.has(`${s.startTicks}-${s.endTicks}`)) out.add(s.stratumId);
  }
  return out;
}

export function FillFromShots({ asset, strata, segments, range, onChanged, tc }: {
  asset: AssetDetail;
  /** The user layers (not system, not hidden). */
  strata: StratumDTO[];
  segments: SegmentDTO[];
  /** The player's In and Out (seconds), read when the action runs. */
  range: () => { in: number | null; out: number | null } | undefined;
  onChanged: () => void;
  /** Timecode text of ticks (the In–Out choice's label). */
  tc?: (ticks: number) => string;
}) {
  const me = useMe().data;
  const [layerId, setLayerId] = useState<string | null>(null);
  const [scope, setScope] = useState<'all' | 'range'>('all');
  const [last, setLast] = useState<{ layer: string; text: string } | null>(null);
  const fill = useFillFromShots(asset.id);
  const removeEmpty = useRemoveEmptySegments(asset.id);
  const shots = shotSegments(segments);
  const canEdit = Boolean(me?.permissions.includes('asset.edit_metadata')) && !asset.deleted;
  if (!canEdit || !strata.length || (!shots.length && !asset.shots)) return null;

  const layer = strata.find((s) => s.id === layerId) ?? strata[0]!;
  const r = range();
  const marked = r && r.in != null && r.out != null && r.out > r.in ? { a: r.in, b: r.out } : null;
  const empties = segments.filter((s) => s.stratumId === layer.id && isEmptySegment(s)).length;
  const changed = layersWithChangedShots(segments, shots);
  const busy = fill.isPending || removeEmpty.isPending;

  async function doFill() {
    const now = range();
    const useRange = scope === 'range' && now && now.in != null && now.out != null && now.out > now.in;
    try {
      const res = await fill.mutateAsync({
        stratumId: layer.id,
        ...(useRange ? { startTicks: Math.round(now.in! * TICKS_PER_SECOND), endTicks: Math.round(now.out! * TICKS_PER_SECOND) } : {}),
      });
      const text = fillResultText(res);
      setLast({ layer: layer.id, text });
      toast(`${layer.label}: ${text}`);
      onChanged();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function doRemove() {
    if (!window.confirm(`Remove the ${empties} empty segment${empties === 1 ? '' : 's'} on “${layer.label}”? Segments with text or values stay.`)) return;
    try {
      const res = await removeEmpty.mutateAsync(layer.id);
      const text = `${res.removed} empty segment${res.removed === 1 ? '' : 's'} removed`;
      setLast({ layer: layer.id, text });
      toast(`${layer.label}: ${text}`);
      onChanged();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <div className="fill-from-shots" data-testid="fill-from-shots">
      <ScanLine size={13} className="muted" aria-hidden />
      <span className="fill-label">{shots.length ? `${shots.length} shot${shots.length === 1 ? '' : 's'}` : 'Shots'}</span>
      <select className="select sm" aria-label="Layer to fill" value={layer.id} onChange={(e) => setLayerId(e.target.value)} data-testid="fill-layer">
        {strata.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
      <select className="select sm" aria-label="Which shots" value={marked ? scope : 'all'} onChange={(e) => setScope(e.target.value as 'all' | 'range')} data-testid="fill-scope">
        <option value="all">Whole asset</option>
        <option value="range" disabled={!marked}>{marked && tc ? `In–Out ${tc(Math.round(marked.a * TICKS_PER_SECOND))}–${tc(Math.round(marked.b * TICKS_PER_SECOND))}` : 'In–Out (mark I and O)'}</option>
      </select>
      <button className="btn sm" disabled={busy || !shots.length} onClick={() => void doFill()} title="One empty segment per shot; shots already logged on this layer are skipped" data-testid="fill-from-shots-run">
        {fill.isPending ? <Loader2 size={13} className="spin" /> : <ScanLine size={13} />} Fill from shots
      </button>
      <button className="btn ghost sm" disabled={busy || empties === 0} onClick={() => void doRemove()} title={empties ? `Remove this layer’s ${empties} empty segments` : 'No empty segments on this layer'} data-testid="remove-empty">
        {removeEmpty.isPending ? <Loader2 size={13} className="spin" /> : <Eraser size={13} />} Remove empty segments
      </button>
      {last && last.layer === layer.id && <span className="faint fill-result" data-testid="fill-result">{last.text}</span>}
      {[...changed].map((id) => (
        <span key={id} className="fill-changed" data-testid="shots-changed">
          {strata.find((s) => s.id === id)?.label ?? id}: Shots changed since this layer was filled
        </span>
      ))}
    </div>
  );
}
